import { Socket } from 'phoenix';
import { pushSyncAuth, clearSyncAuth } from './syncAuthBridge';
import { getPhoenixSocketUrl } from '../utils/phoenixSocketUrl';
import { failoverRegion } from './regionService';
import { getClientId } from '../utils/clientId';

let socket = null;
let channel = null;
// Region failover: after this many consecutive transport errors without a
// successful open, rotate to the next WS region and reconnect.
const WS_ERROR_FAILOVER_THRESHOLD = 4;
let wsErrorStreak = 0;
let lastCreds = null;
const planHandlers            = new Set();
const notificationHandlers    = new Set();
const socketOpenHandlers      = new Set();
const socketReplacedHandlers  = new Set();
// Deliberately NEVER cleared by disconnectSocket() — unlike the Sets above,
// these are permanent, module-load-time subscriptions (see
// addSessionEndedListener) that must survive every teardown so they can
// fire on the NEXT one too. Despite living in this file, this signal is NOT
// about sockets — see addSessionEndedListener's doc comment.
const sessionEndedHandlers    = new Set();
const sessionReplacedHandlers = new Set();
const chatNewMessageHandlers  = new Set();
const chatChannelAddedHandlers = new Set();
const chatReadHandlers        = new Set();
const chatChannelUpdatedHandlers = new Set();
const chatChannelRemovedHandlers = new Set();
const chatMessageUpdatedHandlers = new Set();
const chatMessageDeletedHandlers = new Set();
const chatMessageReactedHandlers = new Set();
// Team vault (05.08.2026). Like sessionEndedHandlers above and UNLIKE every
// other Set here, this one is NEVER cleared by disconnectSocket(): its only
// subscriber is teamVaultStore's module-load-time registration, so clearing it
// on logout would silently leave the NEXT session with no team-vault realtime
// at all — the store is already imported, so nothing would ever re-register.
const teamVaultDeltaHandlers = new Set();
// COMMS-07: user:<id> is the sole transport for chat/notification/plan
// pushes and is always-joined for the whole session, but unlike
// chat:{id}/workspace:{id}/team:{id} it never told anyone it had rejoined
// after a gap — a reconnect (blip, background/resume, region failover)
// silently dropped every event that fired while disconnected, forever.
// Fires (via addUserRejoinListener below) on a second-or-later 'ok' on this
// channel's join, mirroring WORKSPACE_REJOINED_EVENT/onRejoin.
const userRejoinHandlers    = new Set();
// Presence (row 38, Stage 2). Like sessionEndedHandlers and
// teamVaultDeltaHandlers above and UNLIKE every other Set here, these two are
// NEVER cleared by disconnectSocket(): their only subscriber is
// userPresenceService's module-load-time registration, so clearing them on
// logout would leave the NEXT session with no presence realtime at all — the
// module is already imported, so nothing would ever re-register.
const presenceUpdateHandlers = new Set();
const presenceSyncHandlers   = new Set();

export function getSocket() {
  return socket;
}

/**
 * The always-joined `user:<id>` Channel, or `null` when there is no socket.
 *
 * Exposed for ONE caller: `services/userPresenceService.js`, which is the only
 * module in `src/` permitted to push a `presence:*` event (the 03.08.2026
 * anti-chattiness decision, narrowed rather than dropped —
 * `presenceBadgeStaticGuards.test.js` enforces the allowlist). Every other
 * user-channel event is bound below and delivered through an add*Listener; if
 * you are reaching for this to push something new, add a binding instead.
 */
export function getUserChannel() {
  return channel;
}

/**
 * Subscribe to socket-open events. Fires whenever the underlying Phoenix socket
 * (re)connects, letting features that need a live socket attach once it is
 * ready — e.g. a workspace channel join requested before login finished
 * connecting. Returns an unsubscribe function.
 */
export function addSocketOpenListener(fn) {
  socketOpenHandlers.add(fn);
  return () => socketOpenHandlers.delete(fn);
}

/**
 * Subscribe to "the socket was just REPLACED" — distinct from onOpen, which
 * already serves other services' one-shot join-retry logic and fires on
 * every (re)connect including the ordinary first one. This fires only when
 * reconnectAfterFailover() swaps in a brand-new Socket object, so
 * team/workspace/chat channel services can tell "the socket I'm holding a
 * Channel on no longer exists" apart from "the same socket just reopened",
 * and clear their stale Channel references instead of leaving an
 * "already joined" guard silently swallow every future rejoin attempt.
 * Returns an unsubscribe function.
 */
export function addSocketReplacedListener(fn) {
  socketReplacedHandlers.add(fn);
  return () => socketReplacedHandlers.delete(fn);
}

/**
 * Subscribe to "this browser session's identity just ended" — fired at the
 * end of disconnectSocket(), which every logout path (hard: authStore's own
 * direct call; soft/third: reached via the generic `auth:logout` listener in
 * useWindowChrome.js, and the connectSocket() defensive teardown at login
 * time — see below) runs. Despite the name of the module it lives in, this
 * signal is NOT about the Phoenix socket specifically — it is the one place
 * a departed session's identity is known to be gone, and every module that
 * mirrors session-derived state (a socket/channel, a retry guard, a cached
 * user id, an unlock flag, a notification map, ...) is meant to subscribe
 * here, once, and reset ITSELF.
 *
 * INVARIANT (SESS-I1) — mirrors the Rust engine's TSW3-I28 ("the self-heal
 * branch must never fire for a device whose user deliberately logged out"):
 * once this signal has fired, no module holding session-derived state may
 * keep acting as though the departed account is still the active session —
 * not "until its own narrower teardown call happens to run" and not "until
 * some mounted component's cleanup effect fires next." A handler that only
 * nulls its own retry guard here while leaving its PRIMARY state (a joined
 * Channel, a Map/Set keyed by the old session, a cached flag, ...) for some
 * other, narrower call path to clear has NOT satisfied this invariant, even
 * though it "subscribed" — see plans/01_08_2026/outstanding/
 * 01-session-lifecycle-owner.md: the guard-only version of this exact
 * pattern is how the orphaned-channel bug (COMMS-04) was reintroduced one
 * layer up from its own fix. A handler registered here must leave nothing
 * behind that identifies the departed account.
 *
 * Why the "guard-only" shape existed in the first place: addSocketOpenListener/
 * addSocketReplacedListener above are consumed by team/workspace/
 * chatChannelService via a local one-shot guard variable (e.g.
 * `if (socketReplacedUnsub) return;`) so each service only ever registers ONE
 * handler into those Sets for its whole module lifetime. disconnectSocket()
 * clears both Sets on every teardown, but nothing else could reach into those
 * services and null their private guard variables — so after the FIRST
 * teardown, every later login's ensureSocketRetry()/ensureSocketReplacedRetry()
 * saw a stale non-null guard and silently refused to re-subscribe,
 * permanently. This signal exists so each service can null its OWN guard(s)
 * (and, per the invariant above, its own primary state) without any teardown
 * call site needing to know it exists.
 *
 * Callers register a listener ONCE, unconditionally, at module load (not
 * behind their own guard — this Set is intentionally never cleared, so a
 * single module-load-time subscription is permanent). Returns an unsubscribe
 * function (unused by the current callers, kept for symmetry with the other
 * add*Listener functions).
 */
export function addSessionEndedListener(fn) {
  sessionEndedHandlers.add(fn);
  return () => sessionEndedHandlers.delete(fn);
}

// Tear down the current socket (keeping all listener sets) and reconnect —
// connectSocket re-attaches every channel binding, and the region override
// changed underneath it, so the new socket lands on the fallback node.
function reconnectAfterFailover() {
  const creds = lastCreds;
  const url = failoverRegion();
  if (!creds) return;
  try {
    channel?.leave();
    socket?.disconnect();
  } catch {}
  socket = null;
  channel = null;
  console.warn('Realtime: switching WS endpoint to', url || 'default');
  // The CURRENT access token, never the one saved at login: connectSocket
  // hands it to the sync engine too, and during an outage this runs every few
  // socket errors — with the login-time token the engine got 401s once the
  // backend was back and the title bar stayed orange until the next scheduled
  // refresh, up to ~14 min (owner report, 05.10.2026).
  connectSocket(localStorage.getItem('authToken') || creds.token, creds.userId);
  // Tell every service that owns Channel(s) on the old (now-dead) socket to
  // clear that stale state and rejoin for real on the new one. Must fire
  // AFTER connectSocket() so getSocket() already returns the new socket by
  // the time listeners run.
  socketReplacedHandlers.forEach((fn) => fn());
}

export function connectSocket(token, userId) {
  // Defensive second line against a missed logout teardown (COMMS-03): if a
  // socket is still open under a DIFFERENT user's identity, never let it
  // keep silently delivering that account's chat/notifications/presence
  // under the new login — force a teardown before adopting new credentials.
  // Ordinary reconnects (token refresh, region failover) pass the SAME
  // userId as lastCreds and never hit this.
  if (socket && lastCreds && String(lastCreds.userId) !== String(userId)) {
    console.warn('Realtime: socket still open for a different user at login; forcing reconnect');
    disconnectSocket();
  }

  // Sync engine gets the same credentials (best-effort, never blocks login).
  pushSyncAuth(token, userId);
  lastCreds = { token, userId };
  if (socket) return;

  // Realtime is best-effort: a socket failure must never break the login flow
  // that calls this. Notably, constructing a ws:// WebSocket from the secure
  // tauri://localhost context (e.g. a localhost-pointed build) throws a
  // synchronous SecurityError — swallow it and let realtime degrade.
  try {
    // Params as a closure: every (re)connect reads the CURRENT access token
    // from storage, so a socket reconnect after a token refresh authenticates
    // with the fresh one instead of the token captured at login time.
    // client_id rides along so channel processes (which never see the HTTP
    // X-Client-Id header) can name this device in presence metas.
    socket = new Socket(getPhoenixSocketUrl(), {
      params: () => ({
        token: localStorage.getItem('authToken') || token,
        client_id: getClientId(),
      }),
    });
    socket.onOpen(() => {
      wsErrorStreak = 0;
      socketOpenHandlers.forEach((fn) => fn());
    });
    socket.onError(() => {
      wsErrorStreak += 1;
      if (wsErrorStreak >= WS_ERROR_FAILOVER_THRESHOLD) {
        wsErrorStreak = 0;
        reconnectAfterFailover();
      }
    });
    socket.connect();

    channel = socket.channel(`user:${userId}`, {});
    channel.on('plan_updated', (payload) => {
      planHandlers.forEach((fn) => fn(payload));
    });
    channel.on('notification', (payload) => {
      notificationHandlers.forEach((fn) => fn(payload));
    });
    // Session cap (2 app devices + 1 CLI session): a login elsewhere evicted
    // the oldest session(s) and named the revoked token ids. Handlers compare
    // against their own token id and force-logout only if it's in that list.
    channel.on('session_replaced', (payload) => {
      sessionReplacedHandlers.forEach((fn) => fn(payload));
    });
    // Chat: global unread + new-conversation discovery reach the client here on
    // the always-joined user channel, so the rail badge updates even when the
    // chat panel is closed and no chat:{id} topic is joined.
    channel.on('chat:new_message', (payload) => {
      chatNewMessageHandlers.forEach((fn) => fn(payload));
    });
    channel.on('chat:channel_added', (payload) => {
      chatChannelAddedHandlers.forEach((fn) => fn(payload));
    });
    // Read receipts arrive here (not on the chat:{id} topic) so a sender sees
    // checkmarks flip even when the conversation isn't the one they have open.
    channel.on('chat:read', (payload) => {
      chatReadHandlers.forEach((fn) => fn(payload));
    });
    channel.on('chat:channel_updated', (payload) => {
      chatChannelUpdatedHandlers.forEach((fn) => fn(payload));
    });
    channel.on('chat:channel_removed', (payload) => {
      chatChannelRemovedHandlers.forEach((fn) => fn(payload));
    });
    // Edits/unsends/reactions for conversations OTHER than the joined one —
    // without these, a cached background buffer keeps showing unsent/stale
    // messages for the rest of the session.
    channel.on('chat:message_updated', (payload) => {
      chatMessageUpdatedHandlers.forEach((fn) => fn(payload));
    });
    channel.on('chat:message_deleted', (payload) => {
      chatMessageDeletedHandlers.forEach((fn) => fn(payload));
    });
    channel.on('chat:message_reacted', (payload) => {
      chatMessageReactedHandlers.forEach((fn) => fn(payload));
    });
    // Team vault: a CONTENTLESS nudge, fanned out per-user to exactly the
    // members whose authorized view changed. The payload is `{ team_id }` and
    // nothing else — no folder id, no name, no operation, no secret. Anything
    // richer on a topic this broad would let a grant-less member enumerate the
    // folder set and infer the team's credential-rotation timing. Each client
    // pulls its OWN authorized view on receipt, which is also what makes the
    // per-folder permission check the server's job rather than the payload's.
    channel.on('team_vault_delta', (payload) => {
      teamVaultDeltaHandlers.forEach((fn) => fn(payload));
    });
    // Presence for OTHER people (row 38, Stage 2). `presence:sync` is the whole
    // snapshot for the watch set we last sent; `presence:update` is one user.
    // NOTE (Stage 1 contract, addition 3): the sync arrives as a PUSH and can
    // land BEFORE the `{:ok, %{watching: n}}` reply to presence:watch, so no
    // consumer may gate its sync handling on that reply.
    channel.on('presence:update', (payload) => {
      presenceUpdateHandlers.forEach((fn) => fn(payload));
    });
    channel.on('presence:sync', (payload) => {
      presenceSyncHandlers.forEach((fn) => fn(payload));
    });
    // Phoenix re-sends the join push after a socket reconnect and re-fires its
    // receive hooks (see workspaceChannelService.js for the same contract), so
    // a second 'ok' on this same channel means we rejoined after a gap in
    // which chat/notification/plan broadcasts may have been missed.
    let userJoinOkCount = 0;
    channel.join()
      .receive('ok', () => {
        userJoinOkCount += 1;
        if (userJoinOkCount > 1) userRejoinHandlers.forEach((fn) => fn());
      })
      .receive('error', (err) => console.error('UserChannel join error', err));
  } catch (e) {
    console.warn('Realtime socket unavailable; continuing without it:', e);
    socket = null;
    channel = null;
  }
}

export function disconnectSocket() {
  clearSyncAuth();
  channel?.leave();
  socket?.disconnect();
  channel = null;
  socket = null;
  // Defense in depth: nothing currently reads lastCreds while socket is
  // null, but leave no identity-shaped residue in module state after a
  // teardown rather than relying on every future reader to also re-check
  // `socket` truthiness.
  lastCreds = null;
  // wsErrorStreak carries no account identity, but it's still session-scoped
  // counter state this module survey named explicitly — without this, a
  // streak accumulated right before logout (e.g. 3 of the 4 needed to
  // trigger a region failover) left the NEXT session's brand-new socket only
  // one more transport error away from a premature failover instead of
  // starting clean.
  wsErrorStreak = 0;
  planHandlers.clear();
  notificationHandlers.clear();
  socketOpenHandlers.clear();
  socketReplacedHandlers.clear();
  sessionReplacedHandlers.clear();
  chatNewMessageHandlers.clear();
  chatChannelAddedHandlers.clear();
  chatReadHandlers.clear();
  chatChannelUpdatedHandlers.clear();
  chatChannelRemovedHandlers.clear();
  chatMessageUpdatedHandlers.clear();
  chatMessageDeletedHandlers.clear();
  chatMessageReactedHandlers.clear();
  userRejoinHandlers.clear();
  // Tell every permanent, module-load-time subscriber that this session has
  // ended, so each can reset its OWN state (guards AND primary state — see
  // addSessionEndedListener's invariant doc) instead of silently going stale
  // or leaking the departed identity forward. See addSessionEndedListener
  // for why this Set, unlike the ones above, is never itself cleared. Each
  // handler is isolated in its own try/catch so one misbehaving subscriber
  // can never stop the rest of the session from actually ending.
  sessionEndedHandlers.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.error('sessionEnded handler failed:', err);
    }
  });
}

export function addPlanUpdateListener(fn) {
  planHandlers.add(fn);
  return () => planHandlers.delete(fn);
}

export function addNotificationListener(fn) {
  notificationHandlers.add(fn);
  return () => notificationHandlers.delete(fn);
}

/**
 * Subscribe to "the user:<id> channel just rejoined after a gap" (COMMS-07).
 * Broadcasts sent while disconnected are never replayed, so consumers whose
 * only feed is this channel (chat channel list, notifications, plan) should
 * refetch their state here — the same contract as
 * WORKSPACE_REJOINED_EVENT/chat's onRejoin. Returns an unsubscribe function.
 */
export function addUserRejoinListener(fn) {
  userRejoinHandlers.add(fn);
  return () => userRejoinHandlers.delete(fn);
}

export function addSessionReplacedListener(fn) {
  sessionReplacedHandlers.add(fn);
  return () => sessionReplacedHandlers.delete(fn);
}

export function addChatNewMessageListener(fn) {
  chatNewMessageHandlers.add(fn);
  return () => chatNewMessageHandlers.delete(fn);
}

export function addChatChannelAddedListener(fn) {
  chatChannelAddedHandlers.add(fn);
  return () => chatChannelAddedHandlers.delete(fn);
}

export function addChatReadListener(fn) {
  chatReadHandlers.add(fn);
  return () => chatReadHandlers.delete(fn);
}

export function addChatChannelUpdatedListener(fn) {
  chatChannelUpdatedHandlers.add(fn);
  return () => chatChannelUpdatedHandlers.delete(fn);
}

export function addChatChannelRemovedListener(fn) {
  chatChannelRemovedHandlers.add(fn);
  return () => chatChannelRemovedHandlers.delete(fn);
}

export function addChatMessageUpdatedListener(fn) {
  chatMessageUpdatedHandlers.add(fn);
  return () => chatMessageUpdatedHandlers.delete(fn);
}

export function addChatMessageDeletedListener(fn) {
  chatMessageDeletedHandlers.add(fn);
  return () => chatMessageDeletedHandlers.delete(fn);
}

export function addChatMessageReactedListener(fn) {
  chatMessageReactedHandlers.add(fn);
  return () => chatMessageReactedHandlers.delete(fn);
}

/**
 * Subscribe to the team-vault nudge on `user:<id>`.
 *
 * The payload is `{ team_id }` — a cursor, not data. Consumers must re-pull
 * their authorized view; never treat the payload as describing what changed.
 * Registered at module load by teamVaultStore, and deliberately not cleared by
 * disconnectSocket() (see the Set's declaration).
 */
export function addTeamVaultDeltaListener(fn) {
  teamVaultDeltaHandlers.add(fn);
  return () => teamVaultDeltaHandlers.delete(fn);
}

/**
 * Subscribe to `presence:update` on `user:<id>` — `{ user_id, status }` for one
 * watched user. `status` is one of online/away/busy/offline; `"invisible"`
 * cannot appear here, the server collapses it before fan-out and it is never on
 * the wire towards a third party.
 *
 * Registered once at module load by `userPresenceService`, and deliberately not
 * cleared by disconnectSocket() (see the Set's declaration).
 */
export function addPresenceUpdateListener(fn) {
  presenceUpdateHandlers.add(fn);
  return () => presenceUpdateHandlers.delete(fn);
}

/**
 * Subscribe to `presence:sync` on `user:<id>` — `{ statuses: { "123": "away" } }`,
 * the complete answer for the watch set that was last sent. Ids the viewer is
 * not entitled to see are ABSENT, not reported offline.
 */
export function addPresenceSyncListener(fn) {
  presenceSyncHandlers.add(fn);
  return () => presenceSyncHandlers.delete(fn);
}
